import { useRef, type ChangeEvent, type KeyboardEvent, type ReactNode } from 'react';

import type { TextFieldProps } from '@mui/material/TextField';

import { IconButton } from '@src/shared/ui/Button';
import { ArrowDropDownIcon, ArrowDropUpIcon } from '@src/shared/ui/icons';
import { Box } from '@src/shared/ui/layout';
import Typography from '@src/shared/ui/Typography';

import InputAdornment from './InputAdornment';
import TextInput from './TextInput';

const StyleSheet = {
  arrows: {
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'stretch',
  },
  arrow: {
    borderRadius: 0,
    height: 15,
    width: 24,
    overflow: 'hidden',
    p: 0,
  },
};

type NumberInputProps = Omit<TextFieldProps, 'type'> & {
  groupSeparator?: string;
  hideStepper?: boolean;
  suffix?: ReactNode;
  min?: number;
  max?: number;
  step?: number;
};

function NumberInput(props: NumberInputProps) {
  const {
    groupSeparator,
    hideStepper = false,
    suffix,
    min,
    max,
    step = 1,
    value,
    onChange,
    slotProps,
    ...rest
  } = props;

  const htmlInputSlotProps = (slotProps?.htmlInput ?? {}) as Record<string, unknown>;
  const inputSlotProps = (slotProps?.input ?? {}) as Record<string, unknown>;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const stripGroupSeparators = (inputValue: string) => (
    groupSeparator
      ? inputValue.split(groupSeparator).join('')
      : inputValue
  );
  const formatGroupedValue = (inputValue: unknown) => {
    if (!groupSeparator || (typeof inputValue !== 'string' && typeof inputValue !== 'number')) {
      return inputValue;
    }
    const [integerPart, decimalPart] = String(inputValue).split('.');
    const formattedInteger = integerPart.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      groupSeparator,
    );
    return decimalPart === undefined
      ? formattedInteger
      : `${formattedInteger}.${decimalPart}`;
  };
  const caretPositionFromRawIndex = (
    formattedValue: string,
    rawIndex: number,
  ) => {
    if (!groupSeparator || rawIndex === 0) {
      return rawIndex;
    }
    let rawCharacters = 0;
    for (let index = 0; index < formattedValue.length; index += 1) {
      if (formattedValue[index] !== groupSeparator) {
        rawCharacters += 1;
      }
      if (rawCharacters === rawIndex) {
        return index + 1;
      }
    }
    return formattedValue.length;
  };
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!groupSeparator) {
      onChange?.(event);
      return;
    }

    const input = event.currentTarget;
    const selectionStart = input.selectionStart ?? input.value.length;
    const rawCursorIndex = stripGroupSeparators(
      input.value.slice(0, selectionStart),
    ).length;
    const rawValue = stripGroupSeparators(input.value);
    input.value = rawValue;
    onChange?.(event);

    window.requestAnimationFrame(() => {
      const formattedValue = String(formatGroupedValue(rawValue));
      input.setSelectionRange(
        caretPositionFromRawIndex(formattedValue, rawCursorIndex),
        caretPositionFromRawIndex(formattedValue, rawCursorIndex),
      );
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey) return;

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      handleStep(step);
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      handleStep(-step);
      return;
    }

    const allowed = /[\d.]|Backspace|Delete|ArrowLeft|ArrowRight|Tab|Enter|Home|End/;
    if (!allowed.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleStep = (delta: number) => {
    const input = inputRef.current;
    if (!input) {
      return;
    }

    const currentValue = parseFloat(stripGroupSeparators(input.value)) || 0;
    const next = Math.round((currentValue + delta) * 1e2) / 1e2;

    if (min !== undefined && next < min) {
      return;
    }

    if (max !== undefined && next > max) {
      return;
    }

    const nextStr = String(next);
    
    input.value = nextStr;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));

    onChange?.({
      target: { value: nextStr },
      currentTarget: { value: nextStr },
    } as ChangeEvent<HTMLInputElement>);
  };

  return (
    <TextInput
      ref={rootRef}
      value={formatGroupedValue(value)}
      onChange={handleChange}
      slotProps={{
        ...slotProps,
        htmlInput: {
          inputMode: 'numeric',
          ref: inputRef,
          onKeyDown: handleKeyDown,
          ...htmlInputSlotProps,
        },
        input: {
          ...inputSlotProps,
          sx: {
            '&.MuiInputBase-adornedEnd': {
              paddingRight: 0,
            },
          },
          endAdornment: (
            <InputAdornment position="end">
              {suffix ? (
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ mr: 0.25, userSelect: 'none' }}
                >
                  {suffix}
                </Typography>
              ) : null}
              {!hideStepper ? (
                <Box sx={StyleSheet.arrows}>
                  <IconButton
                    aria-label="Increase value"
                    size="small"
                    sx={{ ...StyleSheet.arrow, alignItems: 'flex-start' }}
                    onClick={() => handleStep(step)}
                    tabIndex={-1}
                  >
                    <ArrowDropUpIcon sx={{ fontSize: '16px', mt: '-1px' }} />
                  </IconButton>
                  <IconButton
                    aria-label="Decrease value"
                    size="small"
                    sx={{ ...StyleSheet.arrow, alignItems: 'flex-end' }}
                    onClick={() => handleStep(-step)}
                    tabIndex={-1}
                  >
                    <ArrowDropDownIcon sx={{ fontSize: '16px', mb: '-1px' }} />
                  </IconButton>
                </Box>
              ) : null}
            </InputAdornment>
          ),
        },
      }}
      {...rest}
    />
  );
}

export default NumberInput;
